import { portalLocale } from "../../i18n/portal.js";
/**
 * The payment element in the portal's browser test (plan-005 OP-37 Task 2, design §18.14.3, IC-50;
 * FR-PAY-4 (a), AC-PAY-4) — as data, so every rule is unit-tested without React.
 *
 * A browser test's order confirmed online makes the runtime publish `customer_order_payment` on the
 * conversation's transcript stream (route `transcript_panel`, design §18.11.1): the `payment_link`
 * chat frame's fields without `type` — the shop, the order's reference, the total, the expiry, the
 * `/p/<code>` link, `is_test` and the element's own words in the conversation's language (IC-55's
 * `element_*`). The console draws the same element the website widget draws, marked test.
 *
 * The same event name carrying only `{ customer_order_payment_id, state }` settles an element already
 * drawn (paid / expired / cancelled / not completed), as the widget's `payment_link_state` frame does.
 * IC-50 names the element event; the state shape is accepted so the pane follows a state the runtime
 * forwards on the same channel, and is simply never sent otherwise.
 *
 * The console has no `payment_page_origin` to compare with, so a link is drawn only when it is an
 * `https:` URL whose path is `/p/<code>` — never a `javascript:` or any other scheme — and an element
 * whose link fails that is not drawn at all.
 */
import { CURRENCY_CODE, money, type Money } from "@wetalk/domain";

/** IC-55's `element_*` words, as the event carries them. */
export interface PaymentElementText {
  readonly element_title: string;
  readonly element_pay_label: string;
  readonly element_reference_label: string;
  /** Contains `{time}`. */
  readonly element_expires: string;
  readonly element_is_test_label: string;
  readonly element_paid: string;
  readonly element_not_completed: string;
  readonly element_expired: string;
}

const PAYMENT_ELEMENT_TEXT_KEY: readonly (keyof PaymentElementText)[] = [
  "element_title",
  "element_pay_label",
  "element_reference_label",
  "element_expires",
  "element_is_test_label",
  "element_paid",
  "element_not_completed",
  "element_expired",
];

export const PAYMENT_ELEMENT_STATE = ["paid", "expired", "cancelled", "not_completed"] as const;
export type PaymentElementState = (typeof PAYMENT_ELEMENT_STATE)[number];

export interface PaymentElementView {
  readonly customerOrderPaymentId: string;
  readonly customerOrderReference: string;
  readonly shopName: string;
  /** Integer minor units and the currency, `EUR` (ADR 0014; §18.14.1 fixes the frame's currency). */
  readonly amount: Money;
  readonly expiresAt: Date;
  readonly url: string;
  readonly isTest: boolean;
  readonly text: PaymentElementText;
  /** `null` while the link can still be paid. */
  readonly state: PaymentElementState | null;
  /** How many transcript lines were shown when it arrived: it is drawn after that many. */
  readonly afterLine: number;
}

export type PaymentElementEvent =
  | { readonly kind: "element"; readonly element: Omit<PaymentElementView, "state" | "afterLine"> }
  | { readonly kind: "state"; readonly customerOrderPaymentId: string; readonly state: PaymentElementState };

type Fields = Readonly<Record<string, unknown>>;

function isRecord(value: unknown): value is Fields {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isText(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

/** Only `https://<host>/p/<code>` is a payment link the console will open. */
export function isPaymentPageUrl(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  return url.protocol === "https:" && /^\/p\/[A-Za-z0-9_-]+$/.test(url.pathname) && url.username === "" && url.password === "";
}

function textOf(value: unknown): PaymentElementText | null {
  if (!isRecord(value)) return null;
  for (const key of PAYMENT_ELEMENT_TEXT_KEY) if (!isText(value[key])) return null;
  return value as unknown as PaymentElementText;
}

function stateOf(value: unknown): PaymentElementState | null {
  for (const state of PAYMENT_ELEMENT_STATE) if (state === value) return state;
  return null;
}

/** One `customer_order_payment` event's data, or `null` when it is not one the console can draw. */
export function paymentElementEventOf(data: unknown): PaymentElementEvent | null {
  if (!isRecord(data) || !isText(data["customer_order_payment_id"])) return null;
  const id = data["customer_order_payment_id"];
  if (data["url"] === undefined) {
    const state = stateOf(data["state"]);
    return state === null ? null : { kind: "state", customerOrderPaymentId: id, state };
  }
  const amountMinor = data["amount_minor"];
  const currency = data["currency"];
  const expiresAt = data["expires_at"];
  const url = data["url"];
  const text = textOf(data["text"]);
  if (
    typeof data["customer_order_reference"] !== "string" ||
    typeof data["shop_name"] !== "string" ||
    typeof amountMinor !== "number" ||
    !Number.isSafeInteger(amountMinor) ||
    amountMinor < 0 ||
    currency !== CURRENCY_CODE ||
    typeof expiresAt !== "string" ||
    typeof url !== "string" ||
    !isPaymentPageUrl(url) ||
    typeof data["is_test"] !== "boolean" ||
    text === null
  ) {
    return null;
  }
  const expires = new Date(expiresAt);
  if (Number.isNaN(expires.getTime())) return null;
  return {
    kind: "element",
    element: {
      customerOrderPaymentId: id,
      customerOrderReference: data["customer_order_reference"],
      shopName: data["shop_name"],
      amount: money(amountMinor, CURRENCY_CODE),
      expiresAt: expires,
      url,
      isTest: data["is_test"],
      text,
    },
  };
}

/**
 * The elements after one event. An element already drawn (a resend) is redrawn in its place and
 * keeps its state; a state for an element never drawn changes nothing; paid is final.
 */
export function withPaymentEvent(
  held: readonly PaymentElementView[],
  event: PaymentElementEvent,
  lineCount: number,
): readonly PaymentElementView[] {
  if (event.kind === "state") {
    return held.map((one) =>
      one.customerOrderPaymentId !== event.customerOrderPaymentId || one.state === "paid"
        ? one
        : { ...one, state: event.state },
    );
  }
  const index = held.findIndex((one) => one.customerOrderPaymentId === event.element.customerOrderPaymentId);
  const before = index === -1 ? undefined : held[index];
  if (before === undefined) return [...held, { ...event.element, state: null, afterLine: lineCount }];
  const next = [...held];
  next[index] = { ...event.element, state: before.state, afterLine: before.afterLine };
  return next;
}

/** The state line's words; `cancelled` has no word of its own in IC-55 — it was not completed. */
export function paymentStateWordsOf(element: PaymentElementView): string | null {
  switch (element.state) {
    case null:
      return null;
    case "paid":
      return element.text.element_paid;
    case "expired":
      return element.text.element_expired;
    case "cancelled":
    case "not_completed":
      return element.text.element_not_completed;
  }
}

/** Transcript lines and payment elements in the order they arrived. */
export type TranscriptEntry<T> =
  | { readonly kind: "line"; readonly line: T; readonly index: number }
  | { readonly kind: "payment"; readonly payment: PaymentElementView };

export function interleavedEntries<T>(
  line: readonly T[],
  payment: readonly PaymentElementView[],
): readonly TranscriptEntry<T>[] {
  const entry: TranscriptEntry<T>[] = [];
  const pending = [...payment];
  const flush = (upTo: number): void => {
    for (let next = pending[0]; next !== undefined && next.afterLine <= upTo; next = pending[0]) {
      pending.shift();
      entry.push({ kind: "payment", payment: next });
    }
  };
  line.forEach((one, index) => {
    flush(index);
    entry.push({ kind: "line", line: one, index });
  });
  for (const rest of pending) entry.push({ kind: "payment", payment: rest });
  return entry;
}

/** The console's clock (the order book's `en-IE`, 24-hour). */
const CLOCK = () => new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-IE"), { hour: "2-digit", minute: "2-digit", hour12: false });

/** `element_expires` with its `{time}` filled in, on the console's clock. */
export function expiresWordsOf(element: PaymentElementView): string {
  return element.text.element_expires.replaceAll("{time}", CLOCK().format(element.expiresAt));
}
